import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Filter, MapPin, ArrowLeft } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import UniversitySearch from '@/components/UniversitySearch';
import FiltersPanel from '@/components/FiltersPanel';
import ListingCard from '@/components/ListingCard';
import ListingModal from '@/components/ListingModal';
import PropertyComparison from '@/components/PropertyComparison';
import ComparisonFloatingButton from '@/components/ComparisonFloatingButton';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePropertyComparison } from '@/hooks/usePropertyComparison';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface SearchFilters {
  universityId?: string;
  priceMax?: number;
  km?: number;
  leaseMonths?: number;
  gender?: 'any' | 'female' | 'male';
  furnished?: boolean;
  roommates?: boolean;
  sort?: 'distance' | 'price' | 'recency' | 'verified';
  page?: number;
}

const SearchListings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useLanguage();
  const [showFilters, setShowFilters] = useState(false);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [selectedListing, setSelectedListing] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  // Property comparison functionality
  const { 
    comparisonList, 
    isComparisonOpen, 
    addToComparison, 
    removeFromComparison, 
    clearComparison, 
    openComparison, 
    closeComparison 
  } = usePropertyComparison();

  const filters: SearchFilters = {
    universityId: searchParams.get('universityId') || undefined,
    priceMax: searchParams.get('priceMax') ? parseInt(searchParams.get('priceMax')!) : undefined,
    km: searchParams.get('km') ? parseInt(searchParams.get('km')!) : undefined,
    leaseMonths: searchParams.get('leaseMonths') ? parseInt(searchParams.get('leaseMonths')!) : undefined,
    gender: searchParams.get('gender') as 'any' | 'female' | 'male' || undefined,
    furnished: searchParams.get('furnished') === 'true' ? true : undefined,
    roommates: searchParams.get('roommates') === 'true' ? true : undefined,
    sort: searchParams.get('sort') as 'distance' | 'price' | 'recency' | 'verified' || undefined,
    page: parseInt(searchParams.get('page') || '1')
  };

  useEffect(() => {
    // Count active filters
    const count = Object.entries(filters).filter(([key, value]) => 
      key !== 'universityId' && key !== 'page' && value !== undefined
    ).length;
    setActiveFilterCount(count);

    // Auto-search when university is selected or any filter changes
    if (filters.universityId) {
      searchListings();
    }
  }, [searchParams]);

  const searchListings = async () => {
    if (!filters.universityId) return;
    
    setLoading(true);
    try {
      console.log('Searching listings for university:', filters.universityId);
      
      let query = supabase
        .from('listings')
        .select(`
          *,
          universities (
            name_en,
            name_ar,
            city
          ),
          profiles (
            full_name
          )
        `)
        .eq('university_id', filters.universityId)
        .eq('approval_status', 'approved')
        .eq('status', 'active')
        .eq('rental_status', 'available'); // Only show available properties

      // Apply filters
      if (filters.priceMax) {
        query = query.lte('price_egp', filters.priceMax);
      }

      if (filters.km) {
        query = query.lte('distance_km', filters.km);
      }

      if (filters.leaseMonths) {
        query = query.lte('lease_min_months', filters.leaseMonths);
      }

      if (filters.gender && filters.gender !== 'any') {
        query = query.in('gender_rules', ['any', filters.gender]);
      }

      if (filters.furnished !== undefined) {
        query = query.eq('furnished', filters.furnished);
      }

      if (filters.roommates !== undefined) {
        query = query.eq('roommates_allowed', filters.roommates);
      }

      // Apply sorting
      switch (filters.sort) {
        case 'distance':
          query = query.order('distance_km', { ascending: true });
          break;
        case 'price':
          query = query.order('price_egp', { ascending: true });
          break;
        case 'recency':
          query = query.order('created_at', { ascending: false });
          break;
        case 'verified':
          query = query.order('verified', { ascending: false });
          break;
        default:
          query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error searching listings:', error);
        toast.error('Failed to search listings');
        setListings([]);
        return;
      }

      console.log('Found listings:', data);

      // Transform the data to match the expected format for both card and modal
      const transformedListings = (data || []).map(listing => ({
        id: listing.id,
        title: listing.title,
        titleAr: listing.title, // Using same title for now, could be enhanced with Arabic translations
        price: listing.price_egp,
        deposit: listing.deposit_egp,
        university: listing.universities?.name_en || 'Unknown University',
        distance: listing.distance_km || 0,
        furnished: listing.furnished,
        verified: listing.verified,
        images: listing.photos && listing.photos.length > 0 ? listing.photos : ['/placeholder.svg'],
        unitType: listing.unit_type === 'apartment' ? 'entire' : listing.unit_type,
        genderRules: listing.gender_rules === 'any' ? 'mixed' : listing.gender_rules,
        responseRate: Math.floor(Math.random() * 30) + 70, // Mock response rate for now
        ownerName: listing.profiles?.full_name || 'Unknown Owner',
        // Additional real data for the modal
        description: listing.description,
        address: listing.address,
        amenities: listing.amenities || [],
        availableFrom: listing.available_from,
        leaseMinMonths: listing.lease_min_months,
        roommatesAllowed: listing.roommates_allowed,
        floorPlanUrl: listing.floor_plan_url,
        videoUrl: listing.video_url,
        whatsapp_link: listing.whatsapp_link,
        google_maps_link: listing.google_maps_link,
        profiles: listing.profiles
      }));
      
      setListings(transformedListings);
    } catch (error) {
      console.error('Error searching listings:', error);
      toast.error('Failed to search listings');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  const updateFilters = (newFilters: Partial<SearchFilters>) => {
    const updated = { ...filters, ...newFilters, page: 1 };
    const newSearchParams = new URLSearchParams();
    
    Object.entries(updated).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        newSearchParams.set(key, value.toString());
      }
    });
    
    setSearchParams(newSearchParams);
  };

  const handleUniversitySelect = (universityId: string) => {
    updateFilters({ universityId });
  };

  const handleViewListing = (listing: any) => {
    console.log('Opening modal for listing:', listing);
    setSelectedListing(listing);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedListing(null);
  };

  return (
    <div className={`min-h-screen flex flex-col ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <Header />
      
      <div className="flex-1 container mx-auto px-4 py-8">
        {/* Search Toolbar - Mobile Optimized */}
        <div className="bg-card rounded-lg border p-4 md:p-6 mb-6">
          {/* Mobile Layout */}
          <div className="md:hidden space-y-4">
            <div className="w-full">
              <UniversitySearch 
                currentLang={language} 
                onUniversitySelect={handleUniversitySelect} 
                selectedUniversity={filters.universityId} 
              />
            </div>
            
            <div className="w-full">
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)} 
                className="relative w-full flex items-center justify-center gap-2"
              >
                <Filter className="h-4 w-4" />
                {t('filters', 'فلاتر', 'Filters')}
                {activeFilterCount > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 rounded-full p-0 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:flex flex-col lg:flex-row gap-4 items-start lg:items-center">
            <div className="flex-1">
              <UniversitySearch 
                currentLang={language} 
                onUniversitySelect={handleUniversitySelect} 
                selectedUniversity={filters.universityId} 
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowFilters(!showFilters)} 
                className="relative"
              >
                <Filter className="h-4 w-4 mr-2" />
                {t('filters', 'فلاتر', 'Filters')}
                {activeFilterCount > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 rounded-full p-0 text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t">
              <FiltersPanel filters={filters} onFiltersChange={updateFilters} />
            </div>
          )}
        </div>

        {/* Results */}
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-4 text-muted-foreground">
                {t('searching', 'البحث...', 'Searching...')}
              </p>
            </div>
          ) : listings.length === 0 && filters.universityId ? (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {t('no_results', 'لا توجد نتائج', 'No Results Found')}
              </h3>
              <p className="text-muted-foreground">
                {t('try_different_filters', 'جرب فلاتر مختلفة أو وسع نطاق البحث', 'Try different filters or expand your search criteria')}
              </p>
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map((listing: any) => (
                <ListingCard 
                  key={listing.id} 
                  listing={listing} 
                  currentLang={language}
                  onViewClick={handleViewListing}
                  onAddToComparison={addToComparison}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium mb-2">
                {t('search_university', 'ابحث عن جامعتك', 'Search for your university')}
              </h3>
              <p className="text-muted-foreground">
                {t('select_university_to_search', 'اختر جامعة لبدء البحث عن السكن', 'Select a university to start searching for housing')}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Floating Comparison Button */}
      <ComparisonFloatingButton
        count={comparisonList.length}
        onOpen={openComparison}
        onClear={clearComparison}
      />

      {/* Property Comparison Modal */}
      {isComparisonOpen && (
        <PropertyComparison
          properties={comparisonList}
          onRemoveProperty={removeFromComparison}
          onClose={closeComparison}
        />
      )}
      
      <Footer />

      {selectedListing && (
        <ListingModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          currentLang={language}
          listing={selectedListing}
          onAddToComparison={addToComparison}
        />
      )}
    </div>
  );
};

export default SearchListings;
